import type {
  RegularWeekDay,
  WorkHoursInterval
} from '@elfsight/widget-business-hours';
import {
  getSpecialDaysFromDate,
  getWeekFromDate,
  mergeDays,
  Weekday
} from '@elfsight/widget-business-hours';
import {
  addDays,
  compareAsc,
  isAfter,
  isWithinInterval,
  max,
  min
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import type { StoreLocatorLocation } from '../../types';

function getWeekSettings(location: StoreLocatorLocation) {
  const {
    daySundayHours,
    daySundayOpen,
    dayMondayHours,
    dayMondayOpen,
    dayTuesdayHours,
    dayTuesdayOpen,
    dayWednesdayHours,
    dayWednesdayOpen,
    dayThursdayHours,
    dayThursdayOpen,
    dayFridayHours,
    dayFridayOpen,
    daySaturdayHours,
    daySaturdayOpen
  } = location;

  return [
    {
      weekday: Weekday.sunday,
      settingsHours: daySundayHours,
      isClosed: !daySundayOpen,
      weekdayIndex: 0
    },
    {
      weekday: Weekday.monday,
      settingsHours: dayMondayHours,
      isClosed: !dayMondayOpen,
      weekdayIndex: 1
    },
    {
      weekday: Weekday.tuesday,
      settingsHours: dayTuesdayHours,
      isClosed: !dayTuesdayOpen,
      weekdayIndex: 2
    },
    {
      weekday: Weekday.wednesday,
      settingsHours: dayWednesdayHours,
      isClosed: !dayWednesdayOpen,
      weekdayIndex: 3
    },
    {
      weekday: Weekday.thursday,
      settingsHours: dayThursdayHours,
      isClosed: !dayThursdayOpen,
      weekdayIndex: 4
    },
    {
      weekday: Weekday.friday,
      settingsHours: dayFridayHours,
      isClosed: !dayFridayOpen,
      weekdayIndex: 5
    },
    {
      weekday: Weekday.saturday,
      settingsHours: daySaturdayHours,
      isClosed: !daySaturdayOpen,
      weekdayIndex: 6
    }
  ];
}

function getSpecialIntervals(location: StoreLocatorLocation) {
  const { specialIntervals } = location;

  return specialIntervals
    .flatMap((day) => {
      const endDate = max([
        new Date(`${day.startDate.date}T00:00`),
        new Date(`${day.endDate.date}T00:00`)
      ]);
      const startDate = min([
        new Date(`${day.startDate.date}T00:00`),
        new Date(`${day.endDate.date}T00:00`)
      ]);

      return {
        id: day.id,
        dates: [startDate, endDate] as [Date, Date],
        settingsHours: day.workingHours || [],
        isClosed: day.type === 'closed',
        description: day.description
      };
    })
    .sort((a, b) => compareAsc(a.dates[0], b.dates[0]));
}

export function getUpcomingDays(location: StoreLocatorLocation) {
  const now = new Date();
  const specialIntervals = getSpecialIntervals(location);
  const specialDays = getSpecialDaysFromDate(specialIntervals, now);
  const week = getWeekSettings(location);
  const weekDays = getWeekFromDate(week, now);

  let days: Omit<RegularWeekDay, 'weekday'>[] = mergeDays(
    weekDays,
    specialDays
  );

  const lastDay = days[days.length - 1];

  if (lastDay) {
    days = days.concat(getWeekFromDate(week, addDays(lastDay.date, 1)));
  }

  return days;
}

export function getClosestWorkHoursInterval(
  location: StoreLocatorLocation,
  givenDate: Date
): WorkHoursInterval {
  const upcomingDays = getUpcomingDays(location);
  const upcomingHours = upcomingDays
    .sort((a, b) => compareAsc(a.date, b.date))
    .filter(({ isClosed, hours }) => !isClosed && hours.length)
    .flatMap(({ hours }) => hours);

  return upcomingHours.filter(([, end]) => isAfter(end, givenDate))[0];
}

export function checkOpenStatus(location: StoreLocatorLocation) {
  const { timeZone } = location;
  const now = timeZone ? utcToZonedTime(new Date(), timeZone) : new Date();
  const closestWorkingHours = getClosestWorkHoursInterval(location, now);

  if (!closestWorkingHours) {
    return;
  }

  const [nextFrom, nextUntil] = closestWorkingHours;

  return isWithinInterval(now, { start: nextFrom, end: nextUntil });
}
